import type { ReportPreviewPayload } from '@diagon-alley/common';
import type { SubmitToolResponseArgs } from '@hooks/useReportingThread';
import { Badge, Button, Card, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';

import styles from './ReportsContainer.module.css';
import { formatCount } from '@utils/currency';

interface ReportPreviewCardProps {
    payload: ReportPreviewPayload;
    toolCallId: string;
    onSubmit?: (args: SubmitToolResponseArgs) => Promise<void>;
}

/**
 * Renders the planned shape of a report. Confirm / Cancel buttons
 * round-trip through POST /tool-response — Confirm tells the agent
 * to proceed with execute-report; Cancel tells it to ask for
 * adjustments.
 */
export default function ReportPreviewCard({
    payload,
    toolCallId,
    onSubmit,
}: ReportPreviewCardProps) {
    const [decision, setDecision] = useState<
        'confirm' | 'cancel' | null
    >(null);
    const [busy, setBusy] = useState(false);

    const choose = async (action: 'confirm' | 'cancel') => {
        if (decision || busy) return;
        setDecision(action);
        if (!onSubmit) return; // display-only fallback

        setBusy(true);
        try {
            await onSubmit({
                toolCallId,
                response: { action },
                displayLabel: action === 'confirm' ? 'Confirmed' : 'Cancelled',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card withBorder radius="md" padding="sm" className={styles.previewCard}>
            <Stack gap="xs">
                <Group justify="space-between" align="center" wrap="nowrap">
                    <Text size="sm" fw={600}>
                        {payload.summary}
                    </Text>
                    <Badge size="sm" variant="light" color="gray">
                        Preview
                    </Badge>
                </Group>

                <Group gap="xs" wrap="wrap">
                    <Badge size="sm" variant="light" color="gray">
                        {payload.templateId} · v{payload.version}
                    </Badge>
                    {typeof payload.estimatedRows === 'number' && (
                        <Badge size="sm" variant="light" color="gray">
                            ~{formatCount(payload.estimatedRows)} rows
                        </Badge>
                    )}
                    {typeof payload.estimatedSeconds === 'number' && (
                        <Badge size="sm" variant="light" color="gray">
                            ~{payload.estimatedSeconds.toFixed(0)}s
                        </Badge>
                    )}
                </Group>

                <Group justify="flex-end" gap="xs">
                    {decision ? (
                        <Text size="xs" c="dimmed">
                            {decision === 'confirm' ? 'Confirmed.' : 'Cancelled.'}
                        </Text>
                    ) : (
                        <>
                            <Button
                                size="xs"
                                variant="subtle"
                                color="gray"
                                onClick={() => choose('cancel')}
                                disabled={busy}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="xs"
                                onClick={() => choose('confirm')}
                                loading={busy}
                            >
                                Run report
                            </Button>
                        </>
                    )}
                </Group>
            </Stack>
        </Card>
    );
}
